import React, { useState, useEffect } from "react";
import { FaPhone, FaCaretDown, FaCaretRight } from "react-icons/fa";
import PromptWriter from "./PromptWriter";
import personaService from "../../services/personaService";
import AdditionalAssignmentProperties from "./AdditionalAssignmentProperties";

const RunAssignmentModal = ({ onRun, onClose }) => {
  const [phoneNumber, setPhoneNumber] = useState(
    localStorage.getItem("debug-recipient-phone-number") || "",
  );
  const [personas, setPersonas] = useState([]);
  const [selectedPersona, setSelectedPersona] = useState(
    localStorage.getItem("debug-selected-persona") || "",
  );
  const [communicationType, setCommunicationType] = useState(
    localStorage.getItem("debug-communication-type") || "voice",
  ); // Default value is "voice"
  const [additionalProperties, setAdditionalProperties] = useState(
    JSON.parse(localStorage.getItem("debug-additional-properties")) || {},
  );
  const [showAdditionalProperties, setShowAdditionalProperties] = useState(
    localStorage.getItem("debug-show-additional-properties") == "true" || false,
  );
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchPersonas = async () => {
      try {
        setLoading(true);
        const allPersonas = await personaService.getAll();
        setPersonas(allPersonas);

        if (allPersonas.length > 0) {
          setSelectedPersona(
            selectedPersona || allPersonas[0].id || allPersonas[0]._id,
          );
        }
      } catch (error) {
        console.error("Error fetching personas:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchPersonas();
  }, []);

  const handlePropertyShow = () => {
    setShowAdditionalProperties(!showAdditionalProperties);
    localStorage.setItem(
      "debug-show-additional-properties",
      !showAdditionalProperties,
    );
  };

  const handlePropertyToggle = ({ key, prop }) => {
    let newProps;
    if (key in additionalProperties) {
      newProps = Object.fromEntries(
        Object.entries(additionalProperties).filter(
          ([propKey]) => propKey != key,
        ),
      );
    } else {
      newProps = { ...additionalProperties, [key]: prop };
    }

    setAdditionalProperties(newProps);
    localStorage.setItem(
      `debug-additional-properties`,
      JSON.stringify(newProps),
    );
  };

  const handlePropertyUpdate = (e) => {
    localStorage.setItem(`debug-additional-properties`, e);
    setAdditionalProperties(JSON.parse(e));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const additionalProps = showAdditionalProperties
      ? additionalProperties
      : {};
    onRun(phoneNumber, selectedPersona, communicationType, additionalProps);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center overflow-y-auto z-[8900]">
      <div className="bg-gray-800 p-6 rounded-lg w-[500px] m-4">
        <form onSubmit={handleSubmit}>
          <div className="flex items-start mb-4">
            <img
              src={"/default-avatar.png"}
              alt={"Recipient Phone Number"}
              className="w-16 h-16 rounded-full object-cover mr-4"
            />
            <div className="flex-grow">
              <label className="block text-gray-400">Phone Number</label>
              <div className="relative mb-3">
                <FaPhone className="absolute mt-2 left-2 top-3 text-gray-400" />
                <input
                  type="tel"
                  value={phoneNumber}
                  onChange={(e) => {
                    setPhoneNumber(e.target.value);
                    localStorage.setItem(
                      "debug-recipient-phone-number",
                      e.target.value,
                    );
                  }}
                  className="w-full mt-2 pl-8 p-2 bg-gray-700 text-white rounded"
                  placeholder="Phone Number"
                />
              </div>

              <div className="mb-3">
                <label className="block text-gray-400 mb-1">Persona</label>
                <select
                  value={selectedPersona}
                  onChange={(e) => {
                    setSelectedPersona(e.target.value);
                    localStorage.setItem(
                      "debug-selected-persona",
                      e.target.value,
                    );
                  }}
                  className="w-full p-2 bg-gray-700 text-white rounded"
                  disabled={loading || personas.length === 0}
                >
                  {loading ? (
                    <option>Loading personas...</option>
                  ) : personas.length === 0 ? (
                    <option>No personas available</option>
                  ) : (
                    personas.map((persona) => (
                      <option
                        key={persona.id || persona._id}
                        value={persona.id || persona._id}
                      >
                        {persona.name}
                      </option>
                    ))
                  )}
                </select>
              </div>

              <div className="mb-2">
                <label className="block text-gray-400 mb-1">Modality</label>
                <select
                  value={communicationType}
                  onChange={(e) => {
                    setCommunicationType(e.target.value);
                    localStorage.setItem(
                      "debug-communication-type",
                      e.target.value,
                    );
                  }}
                  className="w-full p-2 bg-gray-700 text-white rounded"
                >
                  <option value="voice">Voice</option>
                  <option value="text">Text</option>
                </select>
              </div>

              <div className="mb-2">
                <button
                  type="button"
                  onClick={handlePropertyShow}
                  className="flex items-center text-gray-400 mb-1 hover:text-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 rounded px-1"
                >
                  <span>Additional Fields</span>
                  {showAdditionalProperties ? (
                    <FaCaretDown />
                  ) : (
                    <FaCaretRight />
                  )}
                </button>

                {showAdditionalProperties && (
                  <AdditionalAssignmentProperties
                    properties={additionalProperties}
                    onToggle={handlePropertyToggle}
                    onPropertyUpdate={handlePropertyUpdate}
                  />
                )}
              </div>
            </div>
          </div>
          <div className="flex justify-end">
            <button
              type="button"
              onClick={onClose}
              className="bg-gray-600 text-white px-4 py-2 rounded mr-2 hover:bg-gray-500"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-500"
              disabled={loading || !phoneNumber || !selectedPersona}
            >
              Run
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default RunAssignmentModal;
